import React, { FC, createContext, useEffect, useRef, useState } from 'react';
import equal from 'fast-deep-equal';
import useCell from './useCell';

export const CellContext = createContext<unknown>(undefined);

interface ICell {
  id?: string;
  notifyLoading?: boolean;
}

interface IMemoized {
  Component: FC | keyof JSX.IntrinsicElements;
}

const buildProps = (propsFromData: any, loading: boolean, notifyLoading?: boolean) => {
  const props = { ...propsFromData };
  if (notifyLoading) {
    props.loading = loading;
  }
  return props;
};

const Memoized = React.memo(({ Component, ...props }: IMemoized) => <Component {...props} />);
Memoized.displayName = 'Memoized';

const Cell = ({ id, notifyLoading }: ICell) => {
  const { Component, componentName, props, loadingComponent, loadingData } = useCell(id);

  const [componentProps, setComponentProps] = useState(
    buildProps(props, loadingData, notifyLoading),
  );

  const CurrentComponent = useRef(Component);
  const [, setComponentName] = useState(componentName);

  useEffect(() => {
    if (!loadingComponent && Component) {
      CurrentComponent.current = Component;
      setComponentName(componentName);

      if (!equal(props, componentProps)) {
        setComponentProps(buildProps(props, loadingData, notifyLoading));
      }
    }
    if (!loadingComponent && !Component && !loadingData) {
      CurrentComponent.current = null;
      setComponentName('');

      if (!equal(props, componentProps)) {
        setComponentProps(buildProps(props, loadingData, notifyLoading));
      }
    }
  }, [Component, componentName, loadingComponent, props, loadingData]);

  if (!CurrentComponent.current) {
    return null;
  }

  return (
    <CellContext.Provider value={componentProps}>
      <Memoized Component={CurrentComponent.current} {...componentProps} />
    </CellContext.Provider>
  );
};

export default Cell;
